/**
 * Page Réservations Propriétaire - Pour voir toutes ses réservations en tant que propriétaire
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  Stack,
  Badge,
  useTheme,
  alpha,
  Modal,
  Backdrop,
  Snackbar,
  Fab,
  Drawer,
  keyframes,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  HomeWork as PropertyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as RenterIcon,
  Home as OwnerIcon,
  SwapHoriz as SwapIcon,
  AccountCircle as AccountIcon,
  QrCode as QrCodeIcon,
  QrCodeScanner as QrCodeScannerIcon,
  FilterAlt as FilterIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
  Cancel as CancelIcon,
  RateReview as RateReviewIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { formatCurrency, LOCALES } from '../constants/dashboardConstants';
import { RESERVATION_STATUS } from '../constants/statusCodes';
import { 
  CANCELLATION_MESSAGES, 
  CANCELLATION_TIME_THRESHOLDS, 
  CANCELLATION_PENALTY_TYPE,
  CANCELLED_BY_FRONTEND,
  CACHE_KEYS 
} from '../constants/cancellationConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reservationService } from '../services/reservationService';
import CancellationService from '../services/cancellationService';
import api from '../services/api';
import MessagingInterface from '../components/MessagingInterface';
import ReservationDetailsPage from '../components/ReservationDetailsPage';
import MessageNotificationBadge from '../components/MessageNotificationBadge';
import useMessageNotifications from '../hooks/useMessageNotifications';
import QRCodeGeneratorModal from '../components/QRCodeGeneratorModal';
import QRCodeConfirmationModal from '../components/QRCodeConfirmationModal';
import QRCodeService from '../services/qrCodeService';
import { isDeliveryOrder } from '../utils/deliveryUtils';
import DeliveryTrackingForm from '../components/delivery/DeliveryTrackingForm';
import CancellationModalEnhanced from '../components/CancellationModalEnhanced';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { translateQRError, getQRSuccessMessage, getQRTechnicalError } from '../utils/qrTranslations';
import OwnerReviewDisplay from '../components/reviews/OwnerReviewDisplay';
import OwnerReviewEmpty from '../components/reviews/OwnerReviewEmpty';
import RenterEvaluationForm from '../components/reviews/RenterEvaluationForm';
import OwnerCreatedReview from '../components/reviews/OwnerCreatedReview';
import {
  getOwnerReviewFromCache,
  setOwnerReviewCache,
  clearOwnerReviewCache,
  clearAllOwnerReviewCache,
  getShortCacheTtl
} from '../services/cache/reviewCacheService';
import { DisputeActionBox } from '../components/disputes';
import UserReputationCard from '../components/reputation/UserReputationCard';
import disputeAdminService from '../services/disputeAdminService';

// Composant LoadingOverlay réutilisable
import LoadingOverlay from '../components/common/LoadingOverlay';

// Composant pour les filtres avancés
import ReservationsAdvancedFilters from '../components/reservation/ReservationsAdvancedFilters';

// Gradient moderne
const gradientStyle = {
  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
  color: 'white'
};

const OwnerReservationsPage = ({ embedded = false }) => {
  const { t } = useTranslation(['dashboard','renterEvaluation']);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  
  // États du sidebar (ajout)
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Animation de pulsation pour le FAB
  const pulseAnimation = keyframes`
    0% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
    50% {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 12px 25px rgba(98, 0, 238, 0.5);
    }
    100% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
  `;

  const flameTrailAnimation = keyframes`
    0% {
      background-position: 0% 50%;
      opacity: 0.9;
      transform: translateX(0);
    }
    50% {
      background-position: 100% 50%;
      opacity: 0.7;
      transform: translateX(2px);
    }
    100% {
      background-position: 0% 50%;
      opacity: 0.9;
      transform: translateX(0);
    }
  `;

  // Gestion du sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Navigation vers la vue locataire
  const handleRenterViewNavigation = () => {
    setSidebarOpen(false);
    navigate('/reservations-renter');
  };

  const [reservations, setReservations] = useState([]);
  const [reservationDisputes, setReservationDisputes] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour le modal d'évaluation renter
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [selectedReservationForEval, setSelectedReservationForEval] = useState(null);
  const [existingReviewForEdit, setExistingReviewForEdit] = useState(null);
  // Clés de rafraîchissement pour forcer le remontage de OwnerCreatedReview après création/maj
  const [reviewRefreshKeys, setReviewRefreshKeys] = useState({});
  
  // Mapping fixe entre index d'onglet et paramètre URL ?status=
  const STATUS_PARAM = ['all', 'pending', 'confirmed', 'active', 'completed', 'cancelled'];

  // Stabiliser user.id pour éviter les re-exécutions constantes
  const stableUserId = useMemo(() => {
    return user?.id || null;
  }, [user?.id]);

  // Filtres de période (date de création de la réservation)
  const DATE_FILTERS = [
    { key: 'all', label: t('myReservations.filters.periods.all') },
    { key: 'today', label: t('myReservations.filters.periods.today') },
    { key: 'week', label: t('myReservations.filters.periods.week') },
    { key: 'month', label: t('myReservations.filters.periods.month') },
    { key: '3months', label: t('myReservations.filters.periods.3months') },
    { key: '6months', label: t('myReservations.filters.periods.6months') },
    { key: 'year', label: t('myReservations.filters.periods.year') }
  ];

  const [activeTab, setActiveTab] = useState(() => {
    try {
      // 1) Priorité à l'URL
      const params = new URLSearchParams(window.location.search);
      const statusParam = (params.get('status') || '').toLowerCase();
      const idxFromUrl = STATUS_PARAM.indexOf(statusParam);
      if (idxFromUrl >= 0) return idxFromUrl;

      // 2) Fallback localStorage
      const saved = localStorage.getItem('ownerReservations_activeTab');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  const [lastFetch, setLastFetch] = useState(null);
  const [showCancellationRulesModal, setShowCancellationRulesModal] = useState(false);
  const scrollPositionRef = useRef(0); // Pour sauvegarder la position de scroll
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const [showReservationDetails, setShowReservationDetails] = useState(false);
  const [selectedReservationForDetails, setSelectedReservationForDetails] = useState(null);
  const [shouldScrollToTracking, setShouldScrollToTracking] = useState(false); // Flag pour scroll vers tracking
  const [showMessaging, setShowMessaging] = useState(false);
  const [selectedReservationForChat, setSelectedReservationForChat] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = (params.get('period') || '').toLowerCase();
      if (DATE_FILTERS.some(f => f.key === fromUrl)) return fromUrl;
      return localStorage.getItem('ownerReservations_dateFilter') || 'all';
    } catch {
      return 'all';
    }
  });

  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [temporalFilter, setTemporalFilter] = useState('all');
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(false);
  const [filterHasUnreadMessages, setFilterHasUnreadMessages] = useState(false);
  const [filterHasOpenDispute, setFilterHasOpenDispute] = useState(false);
  const [filterNeedsReview, setFilterNeedsReview] = useState(false);
  const [filterCanBeCancelled, setFilterCanBeCancelled] = useState(false);
  const [filterQrRequired, setFilterQrRequired] = useState(false);
  const [sortByDate, setSortByDate] = useState('default');
  const [sortByAmount, setSortByAmount] = useState('none');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState([]);

  // Filtre type réservation : 'all' | 'listings' | 'products'
  const [reservationTypeFilter, setReservationTypeFilter] = useState('all');

  // Helper : détermine si une réservation est une commande produit
  const isProductReservation = (reservation) =>
    !!reservation?.listing?.booking_settings?.is_product_order;
  
  // Helper : retourne le lien correct pour afficher le détail (produit ou listing)
  const getLinkForReservation = (reservation) => {
    const isProd = isProductReservation(reservation);
    if (isProd && reservation?.listing?.product?.id) {
      return `/product/${reservation.listing.product.id}/order`;
    }
    // Default pour listings classiques
    const listingId = getListingData(reservation).id;
    return `/view-listing/${listingId}`;
  };

  // Configuration des éléments du sidebar pour les réservations propriétaire
  const sidebarItems = [
    {
      title: t('sidebar.dashboard', 'Tableau de bord'),
      icon: <DashboardIcon />,
      action: () => {
        setSidebarOpen(false);
        navigate('/dashboard');
      },
      description: t('sidebar.dashboardDesc', 'Retour au tableau de bord principal')
    },
    { type: 'divider' },
    {
      title: t('sidebar.renterReservations', 'Mes Réservations'),
      icon: <RenterIcon />,
      action: handleRenterViewNavigation,
      active: false,
      description: t('sidebar.renterReservationsDesc', 'Réservations en tant que locataire')
    },
    {
      title: t('sidebar.ownerReservations', 'Mes Locations'),
      icon: <OwnerIcon />,
      action: () => setSidebarOpen(false),
      active: true,
      description: t('sidebar.ownerReservationsDesc', 'Réservations en tant que propriétaire')
    }
  ];

  // Rendu statique en mode embarqué pour éviter le clignotement
  const staticRender = embedded === true;
  const MaybeFade = ({ children, delay = 0 }) => (
    staticRender ? <>{children}</> : <Fade in timeout={300 + delay}>{children}</Fade>
  );

  // États pour les notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  // États pour les modales QR
  const [qrGeneratorOpen, setQrGeneratorOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrType, setQrType] = useState(''); // 'pickup' ou 'return'
  const [qrReservation, setQrReservation] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  
  // États pour le modal d'expédition de livraison
  const [shipmentModalOpen, setShipmentModalOpen] = useState(false);
  const [shipmentReservation, setShipmentReservation] = useState(null);
  const shipmentScrollPositionRef = useRef(0); // Pour sauvegarder la position de scroll
  
  // Ref pour mémoriser la réservation focalisée (restauration du scroll après fermeture de modal)
  const prevFocusedReservationRef = useRef(null);
  // Helper pour positionner un élément en haut de la fenêtre (avec offset si nécessaire)
  const scrollElementToTop = (el, offset = 16) => {
    try {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const docEl = document.documentElement;
      const scrollTop = window.pageYOffset || docEl.scrollTop || document.body.scrollTop || 0;
      // positionner le haut de l'élément légèrement en dessous du bord supérieur (offset)
      const elementTop = rect.top + scrollTop;
      const target = Math.max(0, Math.round(elementTop - offset));
      window.scrollTo({ top: target, behavior: 'smooth' });
    } catch (err) {
      console.warn('scrollElementToTop error', err);
    }
  };

  // Hook pour les notifications de messages
  const { unreadCounts, markConversationAsRead, getUnreadCount } = useMessageNotifications();

  // Fonction pour formater le prix avec la devise de la réservation
  const formatReservationPrice = (amount, reservation) => {
    // Essayer de récupérer la devise depuis différentes sources
    const currency = reservation?.currency || 
                    reservation?.listing?.currency ||
                    reservation?.listing_currency ||
                    'EUR'; // Fallback vers EUR
                    
    return formatCurrency(amount, currency);
  };

  const canLaunchDispute = (reservation) => {
    if (!reservation) {
      return false;
    }
    const allowed = [
      RESERVATION_STATUS.CONFIRMED,
      RESERVATION_STATUS.ACTIVE,
      RESERVATION_STATUS.COMPLETED
    ];
    return allowed.includes(reservation.status);
  };

  // Synchronisation: si l'URL change (reload ou navigation), aligner l'onglet actif
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = (params.get('status') || '').toLowerCase();
    const idxFromUrl = STATUS_PARAM.indexOf(statusParam);
    if (idxFromUrl >= 0 && idxFromUrl !== activeTab) {
      setActiveTab(idxFromUrl);
    }
    const periodParam = (params.get('period') || '').toLowerCase();
    if (periodParam && DATE_FILTERS.some(f => f.key === periodParam) && periodParam !== dateFilter) {
      setDateFilter(periodParam);
    }
  }, [location.search]); // Supprimé activeTab et dateFilter des dépendances pour éviter la boucle

  // Changement de filtre avec persistance URL + localStorage
  const handleFilterChange = (newValue) => {
    setActiveTab(newValue);
    try {
      const params = new URLSearchParams(location.search);
      params.set('status', STATUS_PARAM[newValue]);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      localStorage.setItem('ownerReservations_activeTab', String(newValue));
    } catch {}
  };

  // Changement période + persistance URL/localStorage
  const handleDateFilterChange = (newKey) => {
    setDateFilter(newKey);
    try {
      const params = new URLSearchParams(location.search);
      if (newKey === 'all') params.delete('period');
      else params.set('period', newKey);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      localStorage.setItem('ownerReservations_dateFilter', newKey);
    } catch {}
  };

  // Configuration du cache - spécifique propriétaire
  const CACHE_KEY = `${CACHE_KEYS.OWNER_RESERVATIONS}_${stableUserId}`;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

  // Fonction pour afficher les notifications
  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const refreshInFlightRef = useRef(false);

  // Fonctions de gestion du cache
  const getCachedData = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Vérifier si le cache est encore valide
      if (now - timestamp > CACHE_DURATION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      console.log('🏠 Données propriétaire chargées depuis le cache');
      return data;
    } catch (error) {
      console.error('Erreur lecture cache propriétaire:', error);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCachedData = (data) => {
    try {
      const cacheObject = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
      console.log('🏠 Données propriétaire mises en cache');
    } catch (error) {
      console.error('Erreur mise en cache propriétaire:', error);
    }
  };

  // Chargement des réservations propriétaire
  // Fonction de récupération des réservations - stabilisée avec useCallback
  const fetchOwnerReservations = useCallback(async (useCache = true) => {
    try {
      setLoading(true);
      setError(null);

      const PAGE_SIZE = 9;

      // Vérifier le cache en premier
      if (useCache) {
        const cachedData = getCachedData();
        if (cachedData) {
          const cachedCount = Array.isArray(cachedData) ? cachedData.length : 0;
          const inferredPage = cachedCount > 0 ? Math.max(1, Math.ceil(cachedCount / PAGE_SIZE)) : 1;

          unstable_batchedUpdates(() => {
            setReservations(cachedData);
            setCurrentPage(inferredPage);
            setHasMore(cachedCount >= PAGE_SIZE);
            setLastFetch(new Date());
            setLoading(false);
          });

          // 🔥 BUG FIX CRITIQUE: Charger les disputes même avec cache !
          console.log('📦 Utilisation du cache, mais chargement des disputes...');
          await fetchDisputesForReservations(cachedData);
          return;
        }
      }

      console.log('🏠 Récupération des réservations propriétaire depuis l\'API...');
      
      // Appel API avec l'ownerId de l'utilisateur connecté et inclusion des images
      const response = await reservationService.getOwnerReservations(stableUserId, {
        include: 'listing,listing.images,renter',
        limit: 9,
        page: 1
      });
      
      console.log('🔍 Réponse API complète:', response);
      
      // Le service retourne directement response.data, donc on traite directement
      let reservationsData = [];
      
      // Cas 1: Structure {success: true, data: {reservations: [...], pagination: {...}}}
      if (response && response.success && response.data && response.data.reservations && Array.isArray(response.data.reservations)) {
        reservationsData = response.data.reservations;
        console.log('📊 Structure détectée: response.data.reservations');
      }
      // Cas 2: Structure directe {reservations: [...], pagination: {...}}
      else if (response && response.reservations && Array.isArray(response.reservations)) {
        reservationsData = response.reservations;
        console.log('📊 Structure détectée: response.reservations');
      }
      // Cas 3: Structure {success: true, data: [...]}
      else if (response && response.success && Array.isArray(response.data)) {
        reservationsData = response.data;
        console.log('📊 Structure détectée: response.data (array)');
      }
      // Cas 4: Tableau direct
      else if (response && Array.isArray(response)) {
        reservationsData = response;
        console.log('📊 Structure détectée: response (array direct)');
      }
      // Cas 5: Autre structure dans response.data
      else if (response && response.data && Array.isArray(response.data)) {
        reservationsData = response.data;
        console.log('📊 Structure détectée: response.data fallback');
      }
      
      // S'assurer que c'est toujours un tableau
      if (!Array.isArray(reservationsData)) {
        console.warn('⚠️ Les données de réservation ne sont pas un tableau:', reservationsData);
        reservationsData = [];
      }
      
      console.log('🔍 [FRONTEND HASOWNERREVIEW] Reservations received:', reservationsData.length);
      reservationsData.forEach((res, index) => {
        console.log(`🔍 [FRONTEND HASOWNERREVIEW] Reservation ${index}:`, {
          id: res.id,
          status: res.status,
          hasOwnerReview: res.hasOwnerReview,
          hasRenterReview: res.hasRenterReview,
          renter_id: res.renter_id || res.renterId
        });
      });
      
      setReservations(reservationsData);
      setCachedData(reservationsData);
      setLastFetch(new Date());
      setCurrentPage(1);
      setHasMore(reservationsData.length === PAGE_SIZE);
      console.log(`🏠 ${reservationsData.length} réservations propriétaire récupérées`);
      
      // Load disputes for these reservations
      await fetchDisputesForReservations(reservationsData);
    } catch (err) {
      console.error('Erreur chargement réservations propriétaire:', err);
      setError(err.message || t('myReservations.errors.cannotLoad'));
    } finally {
      setLoading(false);
    }
    }, [stableUserId, t]); // Dépendances stabilisées

  // Fetch disputes for reservations
  // Options:
  // - updateState (default true): merge dans reservationDisputes
  // - returnMap (default false): retourne disputeMap
  const fetchDisputesForReservations = useCallback(async (reservationsList, options = {}) => {
    const { updateState = true, returnMap = false } = options;

    if (!reservationsList || reservationsList.length === 0) {
      return returnMap ? {} : undefined;
    }
    
    try {
      const disputeMap = {};
      
      // 🚀 OPTIMISATION: Batches de 50 réservations pour éviter la limite API
      const reservationIds = reservationsList.map(r => r.id);
      const BATCH_SIZE = 50;
      const batches = [];
      
      // Découper en batches de 50 IDs
      for (let i = 0; i < reservationIds.length; i += BATCH_SIZE) {
        batches.push(reservationIds.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`📦 [fetchDisputesForReservations] ${reservationIds.length} réservations divisées en ${batches.length} batches de ${BATCH_SIZE}`);
      
      try {
        // Traiter chaque batch séquentiellement pour éviter de surcharger l'API
        for (const batch of batches) {
          const response = await disputeAdminService.list({
            reservation_ids: batch.join(','),
            limit: 100 // Largement suffisant pour 50 réservations
          });
        
          // Extraire les disputes de la réponse
          let disputes = [];
          if (Array.isArray(response)) {
            disputes = response;
          } else if (Array.isArray(response?.data)) {
            disputes = response.data;
          } else if (Array.isArray(response?.results)) {
            disputes = response.results;
          }
          
          // Grouper les disputes par reservation_id
          disputes.forEach(dispute => {
            const resId = dispute.reservation_id;
            if (resId && !disputeMap[resId]) {
              // Garder seulement le premier (le plus récent)
              disputeMap[resId] = dispute;
            }
          });
          
          console.log(`🚀 [Batch] ${disputes.length} disputes chargés pour ${batch.length} réservations`);
        }
        
        console.log(`✅ [fetchDisputesForReservations] Total: ${Object.keys(disputeMap).length} disputes pour ${reservationIds.length} réservations`);
      } catch (apiError) {
        // Fallback: Si l'API ne supporte pas les multiples IDs, faire des appels parallèles
        console.warn('⚠️ API groupée non supportée, fallback vers appels parallèles');
        
        const disputePromises = reservationsList.map(async (reservation) => {
          try {
            const response = await disputeAdminService.list({ reservation_id: reservation.id });
            let disputes = [];
            if (Array.isArray(response)) {
              disputes = response;
            } else if (Array.isArray(response?.data)) {
              disputes = response.data;
            } else if (Array.isArray(response?.results)) {
              disputes = response.results;
            }
            return { reservationId: reservation.id, disputes };
          } catch (err) {
            console.error(`Failed to fetch disputes for reservation ${reservation.id}:`, err);
            return { reservationId: reservation.id, disputes: [] };
          }
        });
        
        // ⚡ Exécuter tous les appels EN PARALLÈLE au lieu de séquentiellement
        const results = await Promise.all(disputePromises);
        
        results.forEach(({ reservationId, disputes }) => {
          if (disputes.length > 0) {
            disputeMap[reservationId] = disputes[0];
          }
        });
        
        console.log(`⚡ [fetchDisputesForReservations] ${results.length} appels parallèles effectués`);
      }
      
      if (updateState) {
        // 🔥 CORRECTION: MERGER avec les disputes existants au lieu d'écraser
        setReservationDisputes(prev => {
          const merged = { ...prev, ...disputeMap };
          console.log(
            `✅ Disputes mergés: ${Object.keys(prev).length} existants + ${Object.keys(disputeMap).length} nouveaux = ${Object.keys(merged).length} total`
          );
          return merged;
        });
      }

      return returnMap ? disputeMap : undefined;
    } catch (err) {
      console.error('Error fetching disputes:', err);
      return returnMap ? {} : undefined;
    }
  }, []);

  // Rafraîchissement qui conserve la pagination (ré-fetch pages 1..currentPage)
  const refreshLoadedReservationsPreservePagination = useCallback(async () => {
    if (refreshInFlightRef.current) {
      console.log('⏳ [refreshLoadedReservationsPreservePagination] Refresh déjà en cours, ignoré');
      return null;
    }

    console.log(`🔍 [DEBUG refreshLoadedReservationsPreservePagination] DÉBUT - stableUserId=${stableUserId}, currentPage=${currentPage}`);
    
    if (!stableUserId) {
      console.warn('⚠️ stableUserId est null/undefined, abandon du refresh');
      return null;
    }

    refreshInFlightRef.current = true;

    const existingCount = Array.isArray(reservations) ? reservations.length : 0;
    const pagesFromState = Math.max(1, currentPage || 1);
    const pagesFromCount = existingCount > 0 ? Math.max(1, Math.ceil(existingCount / 9)) : 1;
    const pagesToFetch = Math.max(pagesFromState, pagesFromCount);
    console.log(
      `🔄 [refreshLoadedReservationsPreservePagination] Rechargement de ${pagesToFetch} pages (currentPage=${currentPage}, existingCount=${existingCount})`
    );
    
    try {
      const extractReservations = (response) => {
        if (response?.success && Array.isArray(response?.data?.reservations)) return response.data.reservations;
        if (Array.isArray(response?.data?.data?.reservations)) return response.data.data.reservations;
        if (Array.isArray(response?.data?.reservations)) return response.data.reservations;
        if (Array.isArray(response?.reservations)) return response.reservations;
        if (response?.success && Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      };

      const pagePromises = [];
      for (let page = 1; page <= pagesToFetch; page += 1) {
        console.log(`📤 Requête API pour page ${page}`);
        pagePromises.push(
          reservationService.getOwnerReservations(stableUserId, {
            include: 'listing,listing.images,renter',
            limit: 9,
            page
          })
        );
      }

      const responses = await Promise.all(pagePromises);
      console.log(`✅ [refreshLoadedReservationsPreservePagination] ${responses.length} réponses API reçues`);
      
      const allReservations = [];
      responses.forEach((response, idx) => {
        const pageData = extractReservations(response);
        if (idx === 0) {
          console.log('🔍 [refreshLoadedReservationsPreservePagination] Response[0] shape:', {
            keys: response ? Object.keys(response) : null,
            success: response?.success,
            dataKeys: response?.data ? Object.keys(response.data) : null,
            reservationsLen_data_reservations: Array.isArray(response?.data?.reservations)
              ? response.data.reservations.length
              : null,
            reservationsLen_data_data_reservations: Array.isArray(response?.data?.data?.reservations)
              ? response.data.data.reservations.length
              : null,
            reservationsLen_reservations: Array.isArray(response?.reservations) ? response.reservations.length : null
          });
        }
        console.log(`📄 Page ${idx + 1}: ${pageData.length} réservations`);
        allReservations.push(...pageData);
      });

      console.log(`📊 Total avant dédupe: ${allReservations.length} réservations`);
      
      const byId = new Map();
      allReservations.forEach(r => {
        if (r?.id != null && !byId.has(r.id)) byId.set(r.id, r);
      });
      const mergedReservations = Array.from(byId.values());
      
      console.log(`📊 Total après dédupe: ${mergedReservations.length} réservations`);

      // Sécurité: ne pas écraser une liste existante non-vide par un résultat vide
      if (mergedReservations.length === 0 && existingCount > 0) {
        console.warn(
          `⚠️ Refresh a renvoyé 0 réservation alors qu'on en avait ${existingCount}. On conserve l'état actuel.`
        );
        return reservations;
      }

      const lastResponse = responses[responses.length - 1];
      const lastPageReservations = extractReservations(lastResponse);

      // Charger les disputes SANS setState intermédiaire pour éviter des re-renders multiples
      const disputeMap = await fetchDisputesForReservations(mergedReservations, { updateState: false, returnMap: true });

      // Appliquer toutes les mises à jour d'un coup (1 seul render)
      unstable_batchedUpdates(() => {
        setReservations(mergedReservations);
        setHasMore(lastPageReservations.length === 9);
        setLastFetch(new Date());
        if (disputeMap && Object.keys(disputeMap).length > 0) {
          setReservationDisputes(prev => ({ ...prev, ...disputeMap }));
        }
      });

      setCachedData(mergedReservations);
      return mergedReservations;
    } catch (error) {
      console.error('❌ Erreur lors du refresh avec pagination conservée (owner):', error);
      return null;
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [stableUserId, currentPage, reservations, fetchDisputesForReservations]);

  // Fonction pour vider le cache
  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    console.log('🏠 Cache propriétaire vidé');
  };

  // Fonction pour extraire les données du listing
  const getListingData = (reservation) => {
    // Support des deux formats: listing (correct) et Listing (ancien)
    const listing = reservation.listing || reservation.Listing;
    return {
      id: listing?.id || reservation.listing_id,
      title: listing?.title || 'Matériel non disponible',
      price_daily: listing?.price_daily || 0,
      price_hourly: listing?.price_hourly || 0,
      booking_mode: listing?.booking_mode || 'date_range',
      booking_settings: listing?.booking_settings || {},
      currency: listing?.currency,
      primary_image: listing?.mainImageUrl || listing?.product?.images?.[0] || listing?.images?.[0]?.url || listing?.images?.[0]?.image_url,
      owner: listing?.User || listing?.owner || listing?.Owner,
      address: listing?.address,
      city: listing?.city,
      postal_code: listing?.postal_code
    };
  };

  // Retourne { amount, label } pour l'affichage du prix unitaire sur la card
  const getListingUnitPrice = (listingData) => {
    const { booking_mode, booking_settings, price_daily, price_hourly } = listingData;
    const bs = booking_settings || {};

    const slotPrice = parseFloat(bs.base_price || bs.price_per_slot || 0);
    const unit = bs.unit || 'day';

    if (booking_mode === 'time_slot' || booking_mode === 'time_slot_capacity') {
      if (slotPrice > 0) {
        const label = unit === 'hour' ? t('ownerReservations.pricing.perHour') : t('ownerReservations.pricing.perSlot');
        return { amount: slotPrice, label };
      }
      if (parseFloat(price_hourly) > 0) return { amount: parseFloat(price_hourly), label: t('ownerReservations.pricing.perHour') };
      return { amount: null, label: t('ownerReservations.pricing.perSlot') };
    }

    // date_range : nuit ou jour selon date_range_mode
    const isNightly = bs.date_range_mode === 'nights';
    const label = isNightly ? t('ownerReservations.pricing.perNight') : t('ownerReservations.pricing.perDay');
    if (parseFloat(price_daily) > 0) return { amount: parseFloat(price_daily), label };
    return { amount: null, label };
  };

  // Calcule la durée correcte selon le mode de réservation
  const getReservationDuration = (reservation) => {
    const listing = reservation.listing || reservation.Listing;
    const bs = listing?.booking_settings || {};
    
    // Si on a start_at et end_at (heures précises), calculer sur la durée exacte
    if (reservation.start_at && reservation.end_at) {
      const startDt = new Date(reservation.start_at);
      const endDt = new Date(reservation.end_at);
      const diffMs = endDt - startDt;
      const diffHours = diffMs / (1000 * 60 * 60);
      const diffDays = diffHours / 24;
      
      // Si la durée est >= 1 jour, afficher en jours
      if (diffDays >= 1) {
        const days = Math.round(diffDays);
        return `${days} ${days > 1 ? t('ownerReservations.labels.days') : t('ownerReservations.labels.day')}`;
      }
    }
    
    // Sinon, calculer sur les dates uniquement
    const startDate = new Date(reservation.start_date);
    const endDate = new Date(reservation.end_date);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

    if (bs.date_range_mode === 'nights') {
      const nights = diffDays;
      return `${nights} ${nights > 1 ? t('ownerReservations.labels.nights') : t('ownerReservations.labels.night')}`;
    }
    // Par défaut : jours (inclusif : +1)
    const days = diffDays + 1;
    return `${days} ${days > 1 ? t('ownerReservations.labels.days') : t('ownerReservations.labels.day')}`;
  };

  // Fonction pour obtenir la devise d'une réservation
  const getReservationCurrency = (reservation) => {
    return reservation.currency || 
           reservation.listing?.currency || 
           reservation.Listing?.currency || 
           'EUR';
  };

  // Fonction pour charger plus de réservations (pagination) - stabilisée avec useCallback
  const loadMoreOwnerReservations = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const response = await reservationService.getOwnerReservations(stableUserId, {
        include: 'listing,listing.images,renter',
        limit: 9,
        page: currentPage + 1
      });
      
      let newReservations = [];
      // Cas 1: Structure {success: true, data: {reservations: [...], pagination: {...}}}
      if (response && response.success && response.data && response.data.reservations && Array.isArray(response.data.reservations)) {
        newReservations = response.data.reservations;
      }
      // Cas 2: Structure directe {reservations: [...], pagination: {...}}
      else if (response && response.reservations && Array.isArray(response.reservations)) {
        newReservations = response.reservations;
      }
      // Cas 3: Structure {success: true, data: [...]}
      else if (response && response.success && Array.isArray(response.data)) {
        newReservations = response.data;
      }
      // Cas 4: Tableau direct
      else if (response && Array.isArray(response)) {
        newReservations = response;
      }
      // Cas 5: Autre structure dans response.data
      else if (response && response.data && Array.isArray(response.data)) {
        newReservations = response.data;
      }
      
      // 🔥 CORRECTION CRITIQUE: Charger les disputes AVANT d'ajouter les réservations
      if (newReservations.length > 0) {
        console.log(`🔍 Chargement des disputes pour ${newReservations.length} nouvelles réservations AVANT setReservations`);
        await fetchDisputesForReservations(newReservations);
      }
      
      setReservations(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const uniques = newReservations.filter(r => !existingIds.has(r.id));
        // Désactiver le bouton si la page API contient moins de 9 réservations ou aucun nouvel élément unique
        if (newReservations.length < 9 || uniques.length === 0) {
          setHasMore(false);
        }
        return [...prev, ...uniques];
      });
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [stableUserId, loadingMore, hasMore, currentPage, fetchDisputesForReservations]);

  // Fonction pour gérer l'annulation d'une réservation - VERSION PROPRIÉTAIRE ENRICHIE
  const handleCancelReservation = async (reservationId) => {
    // Trouver la réservation pour la passer au modal
    const reservation = reservations.find(r => r.id === reservationId);
    setReservationToCancel(reservation);
    setShowCancelModal(true);
  };

  // Nouvelle fonction avec gestion des raisons d'annulation propriétaire
  const handleOwnerCancellationWithReason = async (data) => {
    const { reason, analytics, reservation } = data;
    
    try {
      console.log('🚫 Annulation propriétaire avec raison:', {
        reservationId: reservation.id,
        reason,
        analytics
      });
      
      // Fermer le modal et réinitialiser
      setShowCancelModal(false);
      setReservationToCancel(null);
      
      // Afficher un loading ou état de traitement
      setLoading(true);
      
      // Appel du service d'annulation avec raison enrichie et rôle propriétaire
      const result = await CancellationService.cancelReservation(reservation.id, {
        reason,
        owner_notes: `Annulation propriétaire: ${reason}`,
        cancelled_by: CANCELLED_BY_FRONTEND.OWNER, // 🔑 IMPORTANT: Spécifier le rôle propriétaire
        analytics_data: analytics, // 📊 Pour reporting business
        source_context: 'owner_list'
      });
      
      if (result.success) {
        console.log('✅ Annulation propriétaire avec raison réussie:', result.data);
        
        // Actualiser la liste des réservations SANS recharger la page
        clearCache();
        
        // Recharger les données sans perdre la pagination
        await refreshLoadedReservationsPreservePagination();
        
        // Message de succès spécifique propriétaire avec raison
        const successMessage = `${t('ownerReservations.cancellation.success')}${
          result.data.penalty ? 
          ` ${t('ownerReservations.cancellation.noPenaltyNote')}` : 
          ''
        }`;
        showNotification(successMessage, 'success');
        
      } else {
        console.error('❌ Erreur annulation propriétaire:', result.error);
        const displayError = result.error_key ? t(result.error_key, result.error) : result.error;
        showNotification(`${t('ownerReservations.cancellation.errorPrefix')} ${displayError}`, 'error');
      }
      
    } catch (error) {
      console.error('❌ Erreur annulation réservation propriétaire:', error.message);
      showNotification(`${t('myReservations.errors.general')} ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction legacy pour compatibilité (si besoin fallback)
  const confirmCancelReservation = async () => {
    if (!reservationToCancel) return;
    
    try {
      console.log('🚫 Annulation réservation propriétaire (mode legacy):', reservationToCancel.id);
      
      // Fermer le modal et réinitialiser
      setShowCancelModal(false);
      setReservationToCancel(null);
      
      // Afficher un loading ou état de traitement
      setLoading(true);
      
      // Appel du service d'annulation avec rôle propriétaire
      const result = await CancellationService.cancelReservation(reservationToCancel.id, {
        reason: 'Annulation propriétaire',
        owner_notes: 'Annulation initiée par le propriétaire',
        cancelled_by: CANCELLED_BY_FRONTEND.OWNER // 🔑 IMPORTANT: Spécifier le rôle propriétaire
      });
      
      if (result.success) {
        console.log('✅ Annulation propriétaire réussie:', result.data);
        
        // Actualiser la liste des réservations SANS recharger la page
        clearCache();
        
        // Recharger les données sans perdre la pagination
        await refreshLoadedReservationsPreservePagination();
        
        // Message de succès spécifique propriétaire
        const successMessage = `${t('ownerReservations.cancellation.success')}${
          result.data.penalty ? 
          ` ${t('ownerReservations.cancellation.noPenaltyNote')}` : 
          ''
        }`;
        showNotification(successMessage, 'success');
        
      } else {
        console.error('❌ Erreur annulation propriétaire:', result.error);
        const displayError = result.error_key ? t(result.error_key, result.error) : result.error;
        showNotification(`${t('ownerReservations.cancellation.errorPrefix')} ${displayError}`, 'error');
      }
      
    } catch (error) {
      console.error('❌ Erreur annulation réservation propriétaire:', error.message);
      showNotification(`${t('myReservations.errors.general')} ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    setReservationToCancel(null);
  };

  // Fonctions pour la messagerie
  const handleOpenMessaging = async (reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir la messagerie
    prevFocusedReservationRef.current = reservation?.id || null;
    setSelectedReservationForChat(reservation);
    setShowMessaging(true);
    
    // Marquer la conversation comme lue
    await markConversationAsRead(reservation.id);
  };

  const handleCloseMessaging = () => {
    setShowMessaging(false);
    setSelectedReservationForChat(null);
    // Restaurer le focus/scroll sur la carte précédemment ouverte
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId) {
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el) {
            scrollElementToTop(el, 24);
          }
          prevFocusedReservationRef.current = null;
        }, 50);
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture messaging modal (owner)', err);
    }
  };

  // Fonctions pour les détails de réservation
  const handleViewDetails = (reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal de détails
    prevFocusedReservationRef.current = reservation?.id || null;
    setSelectedReservationForDetails(reservation);
    setShowReservationDetails(true);
  };

  const handleCloseDetails = (hasDataChanged = false) => {
    setShowReservationDetails(false);
    setSelectedReservationForDetails(null);
    // Forcer le rafraîchissement si des modifications ont été effectuées
    if (hasDataChanged) {
      console.log('🔄 Rafraîchissement forcé après modification dans le modal de détails');
      refreshLoadedReservationsPreservePagination();
    }
    // Restaurer le focus/scroll sur la réservation affichée (si aucune modification nécessitant rafraîchissement)
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId && !hasDataChanged) {
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el) {
            scrollElementToTop(el, 24);
          }
          prevFocusedReservationRef.current = null;
        }, 50);
      } else {
        prevFocusedReservationRef.current = null;
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture détails modal (owner)', err);
    }
  };

  // Fonction pour rafraîchir une réservation spécifique dans le modal
  const handleReservationRefresh = async (reservationId) => {
    try {
      console.log('🔄 Rafraîchissement réservation propriétaire spécifique:', reservationId);
      
      clearCache();
      const refreshed = await refreshLoadedReservationsPreservePagination();
      const updatedReservation = (refreshed || reservations).find(r => r.id === reservationId);
      if (updatedReservation) {
        console.log('✅ Réservation propriétaire mise à jour trouvée:', updatedReservation.status);
        setSelectedReservationForDetails(updatedReservation);
      } else {
        console.warn('⚠️ Réservation propriétaire mise à jour non trouvée');
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement de la réservation propriétaire:', error);
    }
  };

  // Fonctions de gestion QR Code
  const handleGenerateQR = useCallback(async (reservationId, type) => {
    // Note: Ne pas mettre qrLoading ici car le modal gère son propre état generating
    try {
      const result = await QRCodeService.generateQRCode(reservationId, type);
      return result;
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error(t('myReservations.qr.generateError'));
      return { success: false, error: 'Erreur technique' };
    }
  }, []);

  // Fonctions de fermeture des modaux QR stabilisées - DÉFINIES EN PREMIER
  const handleCloseQRGenerator = useCallback(() => {
    setQrGeneratorOpen(false);
    // Petit délai pour éviter les conflits d'état lors de la réinitialisation
    setTimeout(() => {
      setQrReservation(null);
      setQrType('');
    }, 100);
  }, []);

  const handleCloseQRScanner = useCallback(() => {
    setQrScannerOpen(false);
    // Petit délai pour éviter les conflits d'état lors de la réinitialisation
    setTimeout(() => {
      setQrReservation(null);
      setQrType('');
    }, 100);
  }, []);

  const handleConfirmation = useCallback(async (confirmationData) => {
    if (qrLoading) return; // Éviter les double clics
    
    setQrLoading(true);
    try {
      let result;
      if (qrType === 'pickup') {
        result = await QRCodeService.confirmPickup(qrReservation.id, confirmationData);
      } else {
        result = await QRCodeService.confirmReturn(qrReservation.id, confirmationData);
      }
      
      if (result.success) {
        toast.success(getQRSuccessMessage(qrType, t));

        const apiUpdatedReservation = result?.data?.reservation;
        
        // 🚀 OPTIMISATION: Mise à jour locale immédiate de la réservation
        setReservations(prev => prev.map(res => {
          if (res.id === qrReservation.id) {
            const updated = { ...res, ...(apiUpdatedReservation || {}) };
            if (qrType === 'pickup') {
              updated.pickup_confirmed = true;
              updated.status = RESERVATION_STATUS.ACTIVE;
              console.log('✅ Pickup confirmé localement pour reservation:', res.id);
            } else {
              updated.return_confirmed = true;
              updated.status = RESERVATION_STATUS.COMPLETED;
              console.log('✅ Retour confirmé localement pour reservation:', res.id);
            }
            return updated;
          }
          return res;
        }));
        
        // Fermer le modal avec la fonction stabilisée
        handleCloseQRScanner();
        
        // Invalider le cache pour garantir la fraîcheur au prochain chargement
        clearCache();
        
        // 🔥 Recharger les disputes pour cette réservation spécifique pour mettre à jour le card
        await fetchDisputesForReservations([{ id: qrReservation.id }]);
      } else {
        toast.error(translateQRError(result, t), {
          autoClose: 8000, // 8 secondes pour que l'utilisateur ait le temps de lire
          position: "top-center" // Position plus visible
        });
        
        // NE PAS fermer le modal pour les erreurs de validation
        // L'utilisateur peut réessayer avec un autre code
      }
    } catch (error) {
      console.error('❌ [OwnerReservationsPage] Exception capturée dans catch:', error);
      toast.error(getQRTechnicalError(t), {
        autoClose: 8000,
        position: "top-center"
      });
      
      // En cas d'erreur, fermer aussi le modal
      handleCloseQRScanner();
    } finally {
      setQrLoading(false);
    }
  }, [qrType, qrReservation?.id, qrLoading, handleCloseQRScanner, t, clearCache]);

  // Handler optimisé pour création de dispute
  const handleDisputeCreated = useCallback(async (newDispute) => {
    console.log('🎯 [handleDisputeCreated] Nouveau dispute créé:', newDispute);
    
    // 1. Mettre à jour uniquement le mapping des disputes (pas de re-fetch complet)
    if (newDispute && newDispute.reservation_id) {
      setReservationDisputes(prev => ({
        ...prev,
        [newDispute.reservation_id]: newDispute
      }));
      console.log('✅ Dispute ajouté localement pour reservation:', newDispute.reservation_id);
    }
    
    // 2. Invalider le cache pour forcer un refresh au prochain chargement
    clearCache();
  }, [clearCache]);

  const handleRefusal = useCallback(async (refusalData) => {
    console.log('🔍 [OwnerReservationsPage] handleRefusal - refusalData reçu:', refusalData);
    console.log('🔍 [OwnerReservationsPage] refusalData.photos:', refusalData.photos);
    console.log('🔍 [OwnerReservationsPage] Raison du refus:', refusalData.refusal_reason);
    
    if (qrLoading) return; // Éviter les double clics
    
    setQrLoading(true);
    try {
      let result;
      if (qrType === 'pickup') {
        console.log('🔍 [OwnerReservationsPage] Appel QRCodeService.refusePickup avec:', qrReservation.id, refusalData);
        result = await QRCodeService.refusePickup(qrReservation.id, refusalData);
      } else {
        // Pour l'instant, seul le refus de pickup est implémenté
        toast.error(t('ownerReservations.refuseNotImplemented'));
        return;
      }
      
      if (result.success) {
        toast.success(t('ownerReservations.pickupRefusedSuccess'));
        
        // Fermer le modal avec la fonction stabilisée
        handleCloseQRScanner();
        
        // Invalider le cache
        clearCache();
      } else {
        toast.error(result.error || t('ownerReservations.refuseError'));
      }
    } catch (error) {
      console.error('Error refusing:', error);
      toast.error(t('ownerReservations.technicalRefuseError'));
      
      // En cas d'erreur, fermer aussi le modal
      handleCloseQRScanner();
    } finally {
      setQrLoading(false);
    }
  }, [qrType, qrReservation?.id, qrLoading, handleCloseQRScanner, t, clearCache]);

  const openQRGenerator = useCallback((reservation, type) => {
    // Fermer tous les autres modaux QR d'abord
    setQrScannerOpen(false);
    
    // Définir les données du nouveau modal
    setQrReservation(reservation);
    setQrType(type);
    setQrGeneratorOpen(true);
  }, []);

  const openQRScanner = useCallback((reservation, type) => {
    // Fermer tous les autres modaux QR d'abord
    setQrGeneratorOpen(false);
    
    // Définir les données du nouveau modal
    setQrReservation(reservation);
    setQrType(type);
    setQrScannerOpen(true);
  }, []);

  // Fonction pour ouvrir le modal d'expédition de livraison
  const openShipmentModal = useCallback((reservation) => {
    // Sauvegarder la position de scroll actuelle
    shipmentScrollPositionRef.current = window.scrollY;
    setShipmentReservation(reservation);
    setShipmentModalOpen(true);
  }, []);

  // Fonction pour fermer le modal d'expédition
  const closeShipmentModal = useCallback(() => {
    setShipmentModalOpen(false);
    setShipmentReservation(null);
    // Restaurer la position de scroll après un court délai
    setTimeout(() => {
      window.scrollTo(0, shipmentScrollPositionRef.current);
    }, 100);
  }, []);

  // Fonction pour gérer le succès de l'expédition
  const handleShipmentSuccess = useCallback(async (updatedReservation) => {
    const reservationId = shipmentReservation?.id;
    
    console.log('📦 [OwnerReservationsPage] handleShipmentSuccess appelé');
    console.log('📦 [OwnerReservationsPage] updatedReservation:', updatedReservation);
    console.log('📦 [OwnerReservationsPage] owner_notes:', updatedReservation?.owner_notes);
    
    if (!reservationId) {
      toast.error('Erreur: ID de réservation manquant');
      setRefreshing(false);
      return;
    }
    
    // Fermer le modal
    setShipmentModalOpen(false);
    setShipmentReservation(null);
    
    // Afficher la notification
    toast.success(t('delivery:confirmShipment') + ' ✓');
    
    // Mettre à jour UNIQUEMENT la card concernée dans le state local
    // Utiliser TOUTES les données retournées par le backend
    setReservations(prevReservations => {
      return prevReservations.map(res => {
        if (res.id === reservationId) {
          // Mise à jour avec les données complètes du backend
          const updated = {
            ...res,
            ...updatedReservation,
            pickup_confirmed: updatedReservation?.pickup_confirmed ?? true,
            return_confirmed: updatedReservation?.return_confirmed ?? true,
            status: updatedReservation?.status ?? 'completed'
          };
          console.log('📦 [OwnerReservationsPage] Réservation mise à jour:', updated);
          console.log('📦 [OwnerReservationsPage] updated.owner_notes:', updated.owner_notes);
          return updated;
        }
        return res;
      });
    });
    
    // Vider le cache pour le prochain chargement
    clearCache();
    
    // Désactiver l'overlay
    setRefreshing(false);
  }, [t, clearCache, shipmentReservation]);
  
  // Fonction pour activer l'overlay avant le chargement
  const handleStartShipmentLoading = useCallback(() => {
    setRefreshing(true);
  }, []);
  
  // Fonction pour gérer les erreurs d'expédition
  const handleShipmentError = useCallback((errorMessage) => {
    console.log('❌ [OwnerReservationsPage] handleShipmentError appelé:', errorMessage);
    // Désactiver l'overlay
    setRefreshing(false);
    // Afficher le message d'erreur
    toast.error(errorMessage);
  }, []);

  // Fonction pour déterminer si une réservation peut être annulée par le propriétaire
  const canCancelReservation = (reservation) => {
    // Utiliser le service local pour un calcul rapide
    return CancellationService.canCancelReservationLocally(reservation);
  };

  // Chargement initial
  useEffect(() => {
    if (!stableUserId) return;

    const isReloadNavigation = (() => {
      try {
        const navEntry = performance?.getEntriesByType?.('navigation')?.[0];
        if (navEntry?.type) return navEntry.type === 'reload';
        // Fallback legacy
        if (performance?.navigation) return performance.navigation.type === 1;
      } catch {
        // ignore
      }
      return false;
    })();

    if (isReloadNavigation) {
      console.log('🔄 [OwnerReservations] Reload navigateur détecté: reset pagination + cache');
      clearCache();
      unstable_batchedUpdates(() => {
        setReservations([]);
        setReservationDisputes({});
        setCurrentPage(1);
        setHasMore(true);
        setLastFetch(null);
      });
      fetchOwnerReservations(false);
      return;
    }

    fetchOwnerReservations(true);
  }, [stableUserId, fetchOwnerReservations]);

  // Fonction de rafraîchissement manuel (bouton refresh)
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Rafraîchissement manuel des réservations');
      console.log(`📌 État actuel: currentPage=${currentPage}, reservations.length=${reservations.length}`);
      // Vider tous les caches : réservations + reviews owner→renter
      clearCache();
      clearAllOwnerReviewCache();
      // Forcer le remontage de tous les OwnerCreatedReview pour relire le cache frais
      setReviewRefreshKeys({});
      await refreshLoadedReservationsPreservePagination(); // Conserve la pagination
    } catch (error) {
      console.error('Erreur lors du refresh:', error);
      toast.error(t('ownerReservations.errors.refreshFailed', 'Erreur lors du rafraîchissement'));
    } finally {
      setRefreshing(false);
    }
  };

  // Handlers pour l'évaluation du renter
  const handleOpenRenterEvaluation = (reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal d'évaluation
    prevFocusedReservationRef.current = reservation?.id || null;
    setSelectedReservationForEval(reservation);
    setEvaluationModalOpen(true);
  };

  const handleCloseRenterEvaluation = () => {
    setEvaluationModalOpen(false);
    setSelectedReservationForEval(null);
    setExistingReviewForEdit(null);
    // Restaurer le focus/scroll après fermeture du modal d'évaluation
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId) {
        // Attendre que le DOM se stabilise après la fermeture du modal + le re-render de la card
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el) {
            scrollElementToTop(el, 24);
            // Tenter de focus l'élément pour l'accessibilité
            el.focus?.({ preventScroll: true });
          }
          prevFocusedReservationRef.current = null;
        }, 300);
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture evaluation modal (owner)', err);
    }
  };

  const handleRenterEvaluationSuccess = (reviewData) => {
    const reservationId = selectedReservationForEval?.id;
    if (reservationId) {
      // Invalider le cache stale et stocker les données fraîches immédiatement
      clearOwnerReviewCache(reservationId);
      if (reviewData) {
        setOwnerReviewCache(reservationId, reviewData);
      }
      setReservations(prev =>
        prev.map(r =>
          r.id === reservationId
            ? { ...r, hasOwnerReview: true }
            : r
        )
      );
      // Forcer le remontage de OwnerCreatedReview pour qu'il lise le cache frais
      setReviewRefreshKeys(prev => ({ ...prev, [reservationId]: (prev[reservationId] || 0) + 1 }));
    }
  };

  // Filtrage par période (création)
  const passesDateFilter = (reservation) => {
    if (!dateFilter || dateFilter === 'all') return true;
    const rawDate = reservation.createdAt || reservation.created_at || reservation.creationDate || reservation.created;
    if (!rawDate) return true; // on n'exclut pas si inconnu
    const created = new Date(rawDate);
    if (Number.isNaN(created.getTime())) return true;
    const now = new Date();
    const msInDay = 24 * 60 * 60 * 1000;
    const sinceDays = (days) => new Date(now.getTime() - days * msInDay);
    let threshold;
    switch (dateFilter) {
      case 'today': {
        threshold = new Date(now);
        threshold.setHours(0, 0, 0, 0);
        return created >= threshold;
      }
      case 'week':
        threshold = sinceDays(7);
        break;
      case 'month':
        threshold = sinceDays(30);
        break;
      case '3months':
        threshold = sinceDays(90);
        break;
      case '6months':
        threshold = sinceDays(180);
        break;
      case 'year':
        threshold = sinceDays(365);
        break;
      default:
        return true;
    }
    return created >= threshold;
  };

  // Filtrage des réservations par statut + période
  const getReservationsByStatus = (status) => {
    // S'assurer que reservations est un tableau
    if (!Array.isArray(reservations)) {
      console.warn('⚠️ reservations n\'est pas un tableau:', reservations);
      return [];
    }
    
    const base = reservations.filter(passesDateFilter);
    if (!status) return base;
    return base.filter(reservation => reservation.status === status);
  };

  // Onglets de filtrage
  const tabs = [
    { 
      label: t('myReservations.filters.all'), 
      value: null, 
      count: getReservationsByStatus(null).length,
      icon: <FilterIcon fontSize="small" />,
      color: "primary"
    },
    { 
      label: t('myReservations.filters.pending'), 
      value: RESERVATION_STATUS.PENDING, 
      count: getReservationsByStatus(RESERVATION_STATUS.PENDING).length,
      icon: <ScheduleIcon fontSize="small" />,
      color: "warning"
    },
    { 
      label: t('myReservations.filters.confirmed'), 
      value: RESERVATION_STATUS.CONFIRMED, 
      count: getReservationsByStatus(RESERVATION_STATUS.CONFIRMED).length,
      icon: <CheckIcon fontSize="small" />,
      color: "info"
    },
    { 
      label: t('myReservations.filters.active'), 
      value: RESERVATION_STATUS.ACTIVE, 
      count: getReservationsByStatus(RESERVATION_STATUS.ACTIVE).length,
      icon: <TrendingUpIcon fontSize="small" />,
      color: "primary"
    },
    { 
      label: t('myReservations.filters.completed'), 
      value: RESERVATION_STATUS.COMPLETED, 
      count: getReservationsByStatus(RESERVATION_STATUS.COMPLETED).length,
      icon: <CheckIcon fontSize="small" />,
      color: "success"
    },
    { 
      label: t('myReservations.filters.cancelled'), 
      value: RESERVATION_STATUS.CANCELLED, 
      count: getReservationsByStatus(RESERVATION_STATUS.CANCELLED).length,
      icon: <CancelIcon fontSize="small" />,
      color: "error"
    }
  ];

  // Logique de recherche et filtrage
  const normalizeForSearch = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const normalizedQuery = normalizeForSearch(searchQuery);

  const matchesSearch = (reservation) => {
    if (!normalizedQuery) return true;

    const listing = getListingData(reservation);

    const renterName =
      reservation?.renter?.fullName ||
      reservation?.renter?.name ||
      reservation?.renterName ||
      '';

    const listingTitle = listing?.title || listing?.name || reservation?.listingTitle || '';

    const city =
      listing?.city ||
      listing?.location?.city ||
      reservation?.city ||
      reservation?.location?.city ||
      '';

    const address =
      listing?.address ||
      listing?.location?.address ||
      listing?.location?.formattedAddress ||
      reservation?.address ||
      reservation?.location?.address ||
      reservation?.location?.formattedAddress ||
      '';

    const reservationId = reservation?.id || reservation?._id || reservation?.reservationId || '';

    // 🔍 DEBUG: Voir les données d'adresse
    console.log('🏠 [ADDRESS DATA - OWNER]', {
      reservationId,
      'listing.address': listing?.address,
      'listing.city': listing?.city || 'N/A',
      finalAddress: address,
      finalCity: city,
      renterName
    });

    const haystack = [listingTitle, renterName, city, address, reservationId]
      .map(normalizeForSearch)
      .filter(Boolean)
      .join(' ');

    return haystack.includes(normalizedQuery);
  };

  // Get temporal category of a reservation
  const getTemporalCategory = (reservation) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const startDate = reservation?.start_date ? new Date(reservation.start_date) : null;
    const endDate = reservation?.end_date ? new Date(reservation.end_date) : null;

    if (!startDate || !endDate) return 'all';

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate > now) return 'upcoming';
    if (startDate <= now && endDate >= now) return 'active';
    if (endDate < now) return 'past';

    return 'all';
  };

  const matchesTemporalFilter = (reservation) => {
    if (temporalFilter === 'all') return true;
    return getTemporalCategory(reservation) === temporalFilter;
  };

  const matchesDateRange = (reservation) => {
    if (!dateRangeStart && !dateRangeEnd) return true;

    const startDate = reservation?.start_date ? new Date(reservation.start_date) : null;
    const endDate = reservation?.end_date ? new Date(reservation.end_date) : null;

    if (!startDate || !endDate) return true;

    if (dateRangeStart) {
      const rangeStart = new Date(dateRangeStart);
      rangeStart.setHours(0, 0, 0, 0);
      if (endDate < rangeStart) return false;
    }

    if (dateRangeEnd) {
      const rangeEnd = new Date(dateRangeEnd);
      rangeEnd.setHours(23, 59, 59, 999);
      if (startDate > rangeEnd) return false;
    }

    return true;
  };

  const matchesUnreadMessages = (reservation) => {
    if (!filterHasUnreadMessages) return true;
    const unreadCount = getUnreadCount(reservation?.id) || 0;
    return unreadCount > 0;
  };

  const matchesOpenDispute = (reservation) => {
    if (!filterHasOpenDispute) return true;
    return !!reservationDisputes[reservation?.id];
  };

  const matchesNeedsReview = (reservation) => {
    if (!filterNeedsReview) return true;
    if (reservation?.status === 'completed') {
      const hasReview = reservation?.reviews && reservation.reviews.length > 0;
      const moderationStatus = reservation?.moderation_status;
      return !hasReview || moderationStatus === 'changes_requested';
    }
    return false;
  };

  const matchesCanBeCancelled = (reservation) => {
    if (!filterCanBeCancelled) return true;
    // Utiliser la vraie logique du CancellationService
    const result = CancellationService.canCancelReservationLocally(reservation);
    // Ne pas afficher si annulation impossible OU si litige ouvert
    const hasDispute = !!reservationDisputes[reservation?.id];
    return result.canCancel === true && !hasDispute;
  };

  const matchesQrRequired = (reservation) => {
    if (!filterQrRequired) return true;
    // Vérifier si pickup ou return QR sont requis
    const pickupPending = !reservation?.pickup_confirmed && ['active', 'confirmed', 'pending'].includes(reservation?.status);
    const returnPending = reservation?.pickup_confirmed && !reservation?.return_confirmed && ['active', 'completed'].includes(reservation?.status);
    return pickupPending || returnPending;
  };

  const matchesPaymentMethod = (reservation) => {
    if (paymentMethodFilter.length === 0) return true;
    // Vérifier la méthode de paiement via les paiements associés
    if (!reservation?.payments || reservation.payments.length === 0) {
      return true; // Si pas de paiements, afficher la réservation
    }
    // Vérifier si au moins un paiement correspond au filtre
    return reservation.payments.some(payment => 
      paymentMethodFilter.includes(payment?.payment_method)
    );
  };

  const currentReservations = useMemo(() => {
    let filtered = tabs[activeTab]?.value ? 
      getReservationsByStatus(tabs[activeTab].value) : 
      getReservationsByStatus(null);
    
    filtered = filtered
      .filter(matchesSearch)
      .filter(matchesTemporalFilter)
      .filter(matchesDateRange)
      .filter(matchesUnreadMessages)
      .filter(matchesOpenDispute)
      .filter(matchesNeedsReview)
      .filter(matchesCanBeCancelled)
      .filter(matchesQrRequired)
      .filter(matchesPaymentMethod)
      .filter(r => {
        if (reservationTypeFilter === 'all') return true;
        const isProd = isProductReservation(r);
        return reservationTypeFilter === 'products' ? isProd : !isProd;
      });

    // Appliquer les tri
    const now = new Date();
    if (sortByDate === 'default') {
      // Tri par date de création (plus récente en premier)
      filtered.sort((a, b) => {
        const dateA = new Date(a?.createdAt || a?.created_at || a?.creationDate || new Date(0));
        const dateB = new Date(b?.createdAt || b?.created_at || b?.creationDate || new Date(0));
        return dateB - dateA;
      });
    } else if (sortByDate === 'newest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a?.start_date) || new Date(0);
        const dateB = new Date(b?.start_date) || new Date(0);
        // Plus proche du maintenant en premier (plus récent = plus proche du now)
        const diffA = Math.abs(dateA - now);
        const diffB = Math.abs(dateB - now);
        return diffA - diffB;
      });
    } else if (sortByDate === 'oldest') {
      filtered.sort((a, b) => {
        const dateA = new Date(a?.start_date) || new Date(0);
        const dateB = new Date(b?.start_date) || new Date(0);
        // Plus éloigné du maintenant en premier (plus ancien = plus loin du now)
        const diffA = Math.abs(dateA - now);
        const diffB = Math.abs(dateB - now);
        return diffB - diffA;
      });
    }

    if (sortByAmount === 'asc') {
      filtered.sort((a, b) => {
        const amountA = parseFloat(a?.total_amount) || 0;
        const amountB = parseFloat(b?.total_amount) || 0;
        return amountA - amountB;
      });
    } else if (sortByAmount === 'desc') {
      filtered.sort((a, b) => {
        const amountA = parseFloat(a?.total_amount) || 0;
        const amountB = parseFloat(b?.total_amount) || 0;
        return amountB - amountA;
      });
    }

    return filtered;
  }, [activeTab, reservations, dateFilter, searchQuery, temporalFilter, dateRangeStart, dateRangeEnd, filterHasUnreadMessages, filterHasOpenDispute, filterNeedsReview, filterCanBeCancelled, filterQrRequired, sortByDate, sortByAmount, paymentMethodFilter, reservationTypeFilter]);

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case RESERVATION_STATUS.PENDING:
        return 'warning';
      case RESERVATION_STATUS.CONFIRMED:
        return 'info';
      case RESERVATION_STATUS.ACTIVE:
        return 'success';
      case RESERVATION_STATUS.COMPLETED:
        return 'primary';
      case RESERVATION_STATUS.CANCELLED:
        return 'error';
      case RESERVATION_STATUS.EXPIRED:
        return 'default';
      default:
        return 'default';
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case RESERVATION_STATUS.PENDING:
        return t('myReservations.status.pending');
      case RESERVATION_STATUS.CONFIRMED:
        return t('myReservations.status.confirmed');
      case RESERVATION_STATUS.ACTIVE:
        return t('myReservations.status.active');
      case RESERVATION_STATUS.COMPLETED:
        return t('myReservations.status.completed');
      case RESERVATION_STATUS.CANCELLED:
        return t('myReservations.status.cancelled');
      case RESERVATION_STATUS.EXPIRED:
        return t('myReservations.status.expired');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <>
        <LoadingOverlay 
          isOpen={true}
          title={t('loading.reservations')}
          subtitle={t('loading.pleaseWait')}
          primaryColor="#6200ee"
          accentColor="#03dac4"
          circleSize={70}
          showWarning={false}
        />
      </>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <PropertyIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {t('ownerReservations.title')} - {t('menu.owner')}
          </Typography>
        </Box>
        
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t('myReservations.errors.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Modal des règles d'annulation propriétaire
  const CancellationRulesModal = () => {
    const handleCloseModal = () => {
      setShowCancellationRulesModal(false);
      // Restaurer la position de scroll après un court délai pour laisser le modal se fermer
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current);
      }, 100);
    };

    return (
      <Modal
        open={showCancellationRulesModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        // Empêcher le scroll vers le haut lors de l'ouverture
        disableScrollLock
      >
        <Fade in={showCancellationRulesModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 800, lg: 900 },
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            {/* Header du modal */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight="600" color="primary">
                {t('cancellationRules.title')}
              </Typography>
              <IconButton 
                onClick={handleCloseModal}
                size="small"
                sx={{ 
                  backgroundColor: alpha(theme.palette.grey[500], 0.1),
                  '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.2) }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <Divider sx={{ mb: 3 }} />

              {/* Message important pour les propriétaires */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  borderRadius: 2
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <InfoIcon sx={{ color: 'info.main', fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="600" color="info.main">
                      {t('cancellationRules.protectionTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('cancellationRules.protectionText')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Contenu des règles spécifiques propriétaire */}
              <Grid container spacing={3}>
                {/* Règle 1 */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      borderRadius: 2,
                      height: '100%'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 24 }} />
                      <Typography variant="h6" fontWeight="600" color="success.main">
                        {t('cancellationRules.rule1Title')}
                      </Typography>
                    </Stack>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: t('cancellationRules.rule1Text') }}
                    />
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                      color: 'success.main',
                      p: 1,
                      borderRadius: 1,
                      fontWeight: 500
                    }}>
                      {t('cancellationRules.rule1Badge')}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Règle 2 */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                      borderRadius: 2,
                      height: '100%'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <MoneyIcon sx={{ color: 'info.main', fontSize: 24 }} />
                      <Typography variant="h6" fontWeight="600" color="info.main">
                        {t('cancellationRules.rule2Title')}
                      </Typography>
                    </Stack>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: t('cancellationRules.rule2Text') }}
                    />
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      backgroundColor: alpha(theme.palette.info.main, 0.2),
                      color: 'info.main',
                      p: 1,
                      borderRadius: 1,
                      fontWeight: 500
                    }}>
                      {t('cancellationRules.rule2Badge')}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Règle 3 */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                      borderRadius: 2,
                      height: '100%'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <TrendingUpIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                      <Typography variant="h6" fontWeight="600" color="warning.main">
                        {t('cancellationRules.rule3Title')}
                      </Typography>
                    </Stack>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: t('cancellationRules.rule3Text') }}
                    />
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      backgroundColor: alpha(theme.palette.warning.main, 0.2),
                      color: 'warning.main',
                      p: 1,
                      borderRadius: 1,
                      fontWeight: 500
                    }}>
                      {t('cancellationRules.rule3Badge')}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Règle 4 */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                      borderRadius: 2,
                      height: '100%'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                      <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} />
                      <Typography variant="h6" fontWeight="600" color="error.main">
                        {t('cancellationRules.rule4Title')}
                      </Typography>
                    </Stack>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: t('cancellationRules.rule4Text') }}
                    />
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      backgroundColor: alpha(theme.palette.error.main, 0.2),
                      color: 'error.main',
                      p: 1,
                      borderRadius: 1,
                      fontWeight: 500
                    }}>
                      {t('cancellationRules.rule4Badge')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Footer du modal */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModal}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 4
                  }}
                >
                  {t('common.ok')}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
      );
    };

  // Wrapper conditionnel pour le mode embarqué
  const ContentWrapper = ({ children }) => {
    if (embedded) {
      return <Box sx={{ p: 0 }}>{children}</Box>;
    }
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {children}
      </Container>
    );
  };

  // Contenu du sidebar
  const SidebarContent = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header du sidebar avec gradient */}
      <Box
        sx={{
          ...gradientStyle,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={toggleSidebar}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          src={user?.avatar}
          sx={{ width: 48, height: 48, border: '2px solid white' }}
        >
          <AccountIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('dashboard.reservations.owner.title', 'Mes Locations')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </Box>

      {/* Menu du sidebar */}
      <Box sx={{ flex: 1, p: 2 }}>
        {sidebarItems.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={index} sx={{ my: 1 }} />;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 1,
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                backgroundColor: item.active 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                borderLeft: item.active 
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={item.action}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: item.active ? theme.palette.primary.main : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: item.active ? 600 : 400,
                      color: item.active ? theme.palette.primary.main : theme.palette.text.primary
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Footer du sidebar */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapIcon />}
          onClick={() => {
            setSidebarOpen(false);
            navigate('/dashboard');
          }}
          sx={{ borderRadius: 2 }}
        >
          {t('dashboard.sidebar.backToDashboard', 'Retour au Hub')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Composant LoadingOverlay réutilisable - avec traductions appliquées */}
      <LoadingOverlay 
        isOpen={refreshing}
        title={t('loading.reservations')}
        subtitle={t('loading.pleaseWait')}
        warning={t('loading.doNotClose')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
        circleSize={70}
        showWarning={true}
      />

      <ContentWrapper>
      

      {/* Onglets de filtrage + filtre période */}
      <Paper sx={{ mb: 3 }}>
        {/* Toggle Locations & Services / Produits */}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: '4px',
              borderRadius: '12px',
              background: theme.palette.background.paper,
              border: '1px solid #e9e9e9',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
            }}
          >
            {[
              { value: 'all', label: t('search.type.both', 'Tout'), Icon: FilterIcon, color: theme.palette.text.primary },
              { value: 'listings', label: t('search.type.listings', 'Locations & Services'), Icon: PropertyIcon, color: theme.palette.primary.main },
              { value: 'products', label: t('search.type.products', 'Produits'), Icon: ShoppingCartIcon, color: theme.palette.success.main }
            ].map(({ value, label, Icon, color }) => {
              const active = reservationTypeFilter === value;
              return (
                <Box
                  key={value}
                  onClick={() => setReservationTypeFilter(value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: { xs: 1.5, md: 2.5 },
                    py: 0.75,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1.5px solid transparent',
                    ...(active ? {
                      background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.2)} 100%)`,
                      borderColor: alpha(color, 0.35),
                      boxShadow: `0 2px 14px ${alpha(color, 0.18)}`
                    } : {
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.05),
                        borderColor: alpha(theme.palette.primary.main, 0.15)
                      }
                    })
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: active ? color : 'text.secondary', transition: 'color 0.2s' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 700 : 400,
                      color: active ? color : 'text.secondary',
                      fontSize: { xs: '0.75rem', md: '0.825rem' },
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => handleFilterChange(newValue)}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 60,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 3,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                },
                borderBottom: 1, borderColor: 'divider'
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      {tab.icon}
                      <span>{tab.label}</span>
                      <Chip 
                        label={tab.count} 
                        size="small" 
                        color={tab.color} 
                        variant="outlined"
                      />
                    </Stack>
                  }
                />
              ))}
            </Tabs>
          </Box>
          
          {/* Actions compactes */}
            <Stack direction="row" alignItems="center" spacing={2}>
              {/* Indicateur de mise à jour compact */}
              {lastFetch && (
                <Chip
                  label={t('myReservations.updatedAgo', { 
                    minutes: Math.round((Date.now() - (lastFetch instanceof Date ? lastFetch.getTime() : lastFetch)) / 1000 / 60) 
                  })}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    color: theme.palette.primary.main,
                    height: 28,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5 }
                  }}
                />
              )}
              
              {/* Bouton refresh compact */}
              {/* Bouton refresh compact */}
              <Tooltip title={t('myReservations.refresh')}>
              <IconButton 
                onClick={handleRefresh}
                disabled={loading}
                size="small"
                sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2)
                },
                '&:disabled': {
                  color: theme.palette.action.disabled
                }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
			  </Tooltip>
            </Stack>
              
          <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 180 }, mr: 1 }}>
            <InputLabel id="owner-period-label">Période</InputLabel>
            <Select
              labelId="owner-period-label"
              id="owner-period-select"
              value={dateFilter}
              label="Période"
              onChange={(e) => handleDateFilterChange(e.target.value)}
            >
              {DATE_FILTERS.map(opt => (
                <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Composant ReservationsAdvancedFilters */}
      <ReservationsAdvancedFilters
        t={t}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        temporalFilter={temporalFilter}
        onTemporalFilterChange={setTemporalFilter}
        dateRangeStart={dateRangeStart}
        dateRangeEnd={dateRangeEnd}
        filtersExpanded={advancedFiltersExpanded}
        onFiltersExpandedChange={setAdvancedFiltersExpanded}
        hasUnreadMessages={filterHasUnreadMessages}
        onHasUnreadMessagesChange={setFilterHasUnreadMessages}
        hasOpenDispute={filterHasOpenDispute}
        onHasOpenDisputeChange={setFilterHasOpenDispute}
        needsReview={filterNeedsReview}
        onNeedsReviewChange={setFilterNeedsReview}
        canBeCancelled={filterCanBeCancelled}
        onCanBeCancelledChange={setFilterCanBeCancelled}
        qrRequired={filterQrRequired}
        onQrRequiredChange={setFilterQrRequired}
        sortByDate={sortByDate}
        onSortByDateChange={setSortByDate}
        sortByAmount={sortByAmount}
        onSortByAmountChange={setSortByAmount}
        paymentMethodFilter={paymentMethodFilter}
        onPaymentMethodFilterChange={setPaymentMethodFilter}
        onDateRangeChange={(start, end) => {
          setDateRangeStart(start);
          setDateRangeEnd(end);
        }}
        onReset={() => {
          setSearchQuery('');
          setTemporalFilter('all');
          setDateRangeStart(null);
          setDateRangeEnd(null);
        }}
      />

      {/* Liste des réservations */}
      {currentReservations.length === 0 ? (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center', 
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <PropertyIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('myReservations.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tabs[activeTab]?.value
                ? t("myReservations.emptyByStatus", { status: tabs[activeTab]?.label.toLowerCase() })
                : t("myReservations.emptyAll")
              }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {currentReservations.map((reservation, idx) => (
            <Grid item xs={12} md={6} lg={4} key={reservation.id}>
              <MaybeFade delay={idx * 80}>
                <Card 
                  id={`reservation-card-${reservation.id}`}
                  tabIndex={-1}
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      transform: 'translateY(-4px)'
                    },
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Image de l'annonce */}
                    {(() => {
                      const listingData = getListingData(reservation);
                      const imageUrl = listingData.primary_image;
                      return imageUrl ? (
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: 160,
                          borderRadius: 2,
                          overflow: 'hidden',
                          mb: 2,
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover img': {
                            transform: 'scale(1.1)'
                          }
                        }}
                        onClick={() => {
                          const url = getLinkForReservation(reservation);
                          const win = window.open(url, '_blank', 'noopener,noreferrer');
                          if (win) win.opener = null;
                        }}
                      >
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={reservation.listing?.title || 'Image de l\'annonce'}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback si image non chargée */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                          }}
                        >
                          <PropertyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {t('ownerReservations.labels.noImage')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: 160,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          flexDirection: 'column'
                        }}
                      >
                        <PropertyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          {t('ownerReservations.labels.noImage')}
                        </Typography>
                      </Box>
                    );
                    })()}
                    
                    {/* En-tête de la carte */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Chip 
                        label={getStatusLabel(reservation.status)}
                        color={getStatusColor(reservation.status)}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          #{reservation.id.slice(-8)}
                        </Typography>
                        {reservation.created_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('ownerReservations.createdAt', { date: new Date(reservation.created_at).toLocaleDateString() })}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Informations de la propriété */}
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        noWrap
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            color: theme.palette.primary.main
                          }
                        }}
                        onClick={() => {
                          const url = getLinkForReservation(reservation);
                          const win = window.open(url, '_blank', 'noopener,noreferrer');
                          if (win) win.opener = null;
                        }}
                      >
                        {reservation.listing?.title || t('ownerReservations.labels.noTitle')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {reservation.listing?.address || t('ownerReservations.labels.noAddress')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Dates */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5, mt: 0.3 }} />
                        <Box>
                          <Typography variant="body2">
                            {new Date(reservation.start_date).toLocaleDateString(LOCALES.FR_FR)}
                            {reservation.start_at && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                 {new Date(reservation.start_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                              </Typography>
                            )}
                            {' — '}
                            {new Date(reservation.end_date).toLocaleDateString(LOCALES.FR_FR)}
                            {reservation.end_at && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {new Date(reservation.end_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {getReservationDuration(reservation)}
                        </Typography>
                      </Box>
                      {reservation.quantity_requested > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.quantityRequested')}:</Typography>
                          <Typography variant="caption" fontWeight="600">
                            {reservation.quantity_requested}
                           {/* {reservation.listing?.stock_quantity && ` / ${reservation.listing.stock_quantity}`} */}
                          </Typography>
                        </Box>
                      )}
                      {reservation.seats_requested > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.seatsRequested')}:</Typography>
                          <Typography variant="caption" fontWeight="600">
                            {reservation.seats_requested}
                            {/* {reservation.listing?.capacity_default && ` / ${reservation.listing.capacity_default}`} */}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Montant */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {
                            (() => {
                              const payments = reservation?.payments || [];
                              const methods = payments.map(p => (p?.payment_method || '').toLowerCase());
                              if (methods.some(m => m.includes('cash'))) {
                                return (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box component="span">💰</Box>
                                    <Box component="span" sx={{ fontWeight: 700, fontSize: 13 }}>Cash</Box>
                                  </Box>
                                );
                              }
                              if (methods.some(m => m.includes('stripe') || m.includes('card') || m.includes('credit'))) {
                                return (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box component="span">💳</Box>
                                    <Box component="span" sx={{ fontWeight: 700, fontSize: 13 }}>Card</Box>
                                  </Box>
                                );
                              }
                              return <MoneyIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />;
                            })()
                          }
                        </Box>
                      </Box>
                      <Divider sx={{ width: '40%', my: 0.5 }} />
                      {/* Prix unitaire (sans unité pour les produits) */}
                      {(() => {
                        const ld = getListingData(reservation);
                        const { amount, label } = getListingUnitPrice(ld);
                        const isProd = reservation?.listing?.booking_settings?.is_product_order;
                        return amount != null ? (
                          <Typography variant="body2" fontWeight="600" sx={{ color: theme.palette.success.main, fontSize: '0.85rem', mt: 0.5 }}>
                            {isProd
                              ? formatReservationPrice(amount, reservation)
                              : `${formatReservationPrice(amount, reservation)} ${label}`
                            }
                          </Typography>
                        ) : null;
                      })()}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total:
                        </Typography>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          {formatReservationPrice(reservation.total_amount, reservation)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon sx={{ fontSize: '1.2rem !important', color: 'white' }} />}
                        onClick={() => handleViewDetails(reservation)}
                        sx={{
                          minWidth: 'auto',
                          width: 38,
                          height: 38,
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark
                          },
                          '& .MuiButton-startIcon': {
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }
                        }}
                      >
                      </Button>
                      
                      {/* Bouton de messagerie - toujours disponible sauf pour les réservations annulées/expirées */}
                      {(reservation.status !== RESERVATION_STATUS.CANCELLED && 
                        reservation.status !== RESERVATION_STATUS.EXPIRED) && (
                        <MessageNotificationBadge 
                          reservationId={reservation.id} 
                          unreadCount={getUnreadCount(reservation.id)}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ChatIcon />}
                            color="primary"
                            onClick={() => handleOpenMessaging(reservation)}
                            sx={{ 
                              minWidth: 'auto',
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderColor: theme.palette.primary.dark
                              }
                            }}
                          >
                            {t('myReservations.actions.message')}
                          </Button>
                        </MessageNotificationBadge>
                      )}
                      
                      {/* Boutons QR Code pour confirmations pickup/return OU Label/Bouton livraison */}
                      {(reservation.status === RESERVATION_STATUS.CONFIRMED || reservation.status === RESERVATION_STATUS.ACTIVE) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 'auto' }}>
                          {isDeliveryOrder(reservation) ? (
                            /* Mode Livraison - Afficher un bouton ou label selon état */
                            !reservation.pickup_confirmed ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<LocalShippingIcon />}
                                onClick={() => openShipmentModal(reservation)}
                                color="warning"
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  minHeight: 28
                                }}
                              >
                                {t('delivery:confirmShipmentButton')}
                              </Button>
                            ) : (
                              <Chip
                                icon={<CheckIcon />}
                                label={t('delivery:shipped')}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.75rem'
                                }}
                              />
                            )
                          ) : (
                            /* Mode Pickup/Return classique - Afficher les boutons QR */
                            <>
                              {/* Bouton Scanner Pickup - visible pour le propriétaire si pickup pas confirmé */}
                              {QRCodeService.canScanQRCode(reservation, 'pickup', user?.id) && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<QrCodeScannerIcon />}
                                  onClick={() => openQRScanner(reservation, 'pickup')}
                                  disabled={qrLoading}
                                  color="success"
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minHeight: 28,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {t('ownerReservations.actions.scanPickup')}
                                </Button>
                              )}
                              
                              {/* Bouton QR Return - visible pour le propriétaire si return pas confirmé et pickup fait */}
                              {QRCodeService.canGenerateQRCode(reservation, 'return', user?.id) && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<QrCodeIcon />}
                                  onClick={() => openQRGenerator(reservation, 'return')}
                                  disabled={qrLoading}
                                  color="warning"
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minHeight: 28,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {t('ownerReservations.QRReturn')}
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                      
                      {/* Section d'annulation pour propriétaires */}
                      {(() => {
                        const cancellationInfo = canCancelReservation(reservation);
                        const hasDispute = Boolean(reservationDisputes[reservation.id]);
                        const cancelTooltip = hasDispute
                          ? t('myReservations.cancelDisabledDueToDispute', "Impossible d'annuler : un litige est ouvert pour cette réservation")
                          : (cancellationInfo.canCancel ? `${cancellationInfo.reason} (${cancellationInfo.hoursBeforeStart}h restantes)` : cancellationInfo.reason);

                        return (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                            <Tooltip title={cancelTooltip} arrow>
                              <span style={{ flex: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  color={hasDispute ? 'warning' : 'error'}
                                  disabled={!cancellationInfo.canCancel || hasDispute}
                                  onClick={() => handleCancelReservation(reservation.id)}
                                  fullWidth
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    opacity: (!cancellationInfo.canCancel || hasDispute) ? 0.6 : 1,
                                    minHeight: 32
                                  }}
                                >                                
                                  {t('myReservations.actions.cancel')}
                                </Button>
                              </span>
                            </Tooltip>
                            
                            {/* Bouton d'information rond */}
                            <Tooltip title={t("myReservations.cancellationRules")} arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  scrollPositionRef.current = window.scrollY;
                                  setShowCancellationRulesModal(true);
                                }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: cancellationInfo.canCancel 
                                    ? alpha(theme.palette.success.main, 0.2)
                                    : alpha(theme.palette.warning.main, 0.2),
                                  color: cancellationInfo.canCancel ? 'success.main' : 'warning.main',
                                  '&:hover': {
                                    backgroundColor: cancellationInfo.canCancel 
                                      ? alpha(theme.palette.success.main, 0.3)
                                      : alpha(theme.palette.warning.main, 0.3),
                                  }
                                }}
                              >
                                <InfoIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        );
                      })()}
                    </Box>

                    {/* Section renter - avatar + nom + membre depuis + réputation */}
                    {reservation.renter?.id && (
                      <UserReputationCard
                        userId={reservation.renter.id}
                        user={reservation.renter}
                        fallbackName={t('ownerReservations.labels.noRenter')}
                      />
                    )}

                    {/* Nouvelle section Livraison - au-dessus des Disputes */}
                    {reservation.status === RESERVATION_STATUS.COMPLETED && 
                     isDeliveryOrder(reservation) && 
                     reservation.pickup_confirmed && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          fullWidth
                          onClick={() => {
                            setShouldScrollToTracking(true);
                            handleViewDetails(reservation);
                          }}
                          startIcon={<ViewIcon />}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {t('delivery:viewTracking')}
                        </Button>
                        <Chip
                          icon={<CheckIcon />}
                          label={t('delivery:shipped')}
                          size="medium"
                          color="success"
                          variant="filled"
                          sx={{
                            width: '100%',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                          }}
                        />
                      </Box>
                    )}

                    {(canLaunchDispute(reservation) || Boolean(reservationDisputes[reservation.id])) && (
                      <Box sx={{ mt: 2 }}>
                        <DisputeActionBox
                          reservation={reservation}
                          actorRole="owner"
                          variant="contained"
                          color="warning"
                          size="small"
                          fullWidth
                          disabled={qrLoading}
                          existingDispute={reservationDisputes[reservation.id]}
                          onCreated={handleDisputeCreated}
                        />
                      </Box>
                    )}

                    {/* Bouton évaluer le locataire */}
                    {(() => {
                      const shouldShow = reservation.status === RESERVATION_STATUS.COMPLETED && !reservation.hasOwnerReview;
                      console.log('🔍 [BUTTON RENDER] Reservation:', {
                        id: reservation.id,
                        status: reservation.status,
                        completedStatus: RESERVATION_STATUS.COMPLETED,
                        hasOwnerReview: reservation.hasOwnerReview,
                        shouldShowButton: shouldShow
                      });
                      return shouldShow;
                    })() && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<RateReviewIcon />}
                          onClick={() => handleOpenRenterEvaluation(reservation)}
                          fullWidth
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            fontWeight: 500
                          }}
                        >
                          {t('renterEvaluation:evaluateRenter')}
                        </Button>
                      </Box>
                    )}

                    {/* Affichage de la review owner→renter si elle existe */}
                    {reservation.status === RESERVATION_STATUS.COMPLETED && reservation.hasOwnerReview && (
                      <OwnerCreatedReview 
                        key={`owner-review-${reservation.id}-${reviewRefreshKeys[reservation.id] || 0}`}
                        reservation={reservation}
                        onEdit={async (reservationData, reviewData) => {
                          // Mémoriser la card pour autoscroll après fermeture du modal
                          prevFocusedReservationRef.current = reservationData?.id || null;
                          // Charger la review complète avec tous les détails
                          try {
                            const cachedReview = getOwnerReviewFromCache(reservationData.id);
                            if (cachedReview !== null) {
                              setSelectedReservationForEval(reservationData);
                              setExistingReviewForEdit(cachedReview);
                              setEvaluationModalOpen(true);
                              return;
                            }

                            const response = await api.get(`/reviews/renter/${reservationData.id}`);
                            const fullReview = response.data.data.data;
                            setOwnerReviewCache(reservationData.id, fullReview ?? null);

                            // Ouvrir le formulaire d'évaluation en mode édition
                            setSelectedReservationForEval(reservationData);
                            setExistingReviewForEdit(fullReview);
                            setEvaluationModalOpen(true);
                          } catch (err) {
                            console.error('Error loading review for edit:', err);
                            // Fallback: utiliser les données disponibles
                            setSelectedReservationForEval(reservationData);
                            setExistingReviewForEdit(reviewData);
                            setOwnerReviewCache(
                              reservationData.id,
                              reviewData ?? null,
                              getShortCacheTtl()
                            );
                            setEvaluationModalOpen(true);
                          }
                        }}
                      />
                    )}

                    {/* Affichage de la review du renter si la réservation est complétée */}
                    {reservation.status === RESERVATION_STATUS.COMPLETED && (
                      <Box sx={{ mt: 2 }}>
                        {reservation.reservationReview ? (
                          <OwnerReviewDisplay 
                            reservation={reservation}
                            userRole="owner"
                            onResponseAdded={(updatedReview) => {
                              // Mettre à jour localement la réservation avec la nouvelle réponse
                              setReservations(prev => 
                                prev.map(r => 
                                  r.id === reservation.id 
                                    ? { ...r, reservationReview: updatedReview }
                                    : r
                                )
                              );
                            }}
                          />
                        ) : (
                          <OwnerReviewEmpty />
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </MaybeFade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Section Load More - reste visible même si la recherche retourne 0 résultat */}
      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            {loadingMore && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: 0,
                  right: 0,
                  height: 6,
                  borderRadius: '999px',
                  background: 'linear-gradient(270deg, rgba(255,120,0,0.95), rgba(255,220,0,0.9), rgba(255,120,0,0.95))',
                  backgroundSize: '200% 100%',
                  animation: `${flameTrailAnimation} 1.6s ease-in-out infinite`,
                  filter: 'blur(1px)'
                }}
              />
            )}
            <Button
              type="button"
              onClick={loadMoreOwnerReservations}
              disabled={loadingMore}
              variant="contained"
              color="primary"
              sx={{
                animation: loadingMore ? 'none' : `${pulseAnimation} 1.2s ease-in-out infinite`,
                '&:disabled': {
                  animation: 'none'
                }
              }}
            >
              {loadingMore ? t('myReservations.loadMore.loading') : t('myReservations.loadMore.button')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('myReservations.loadMore.allLoaded')}
          </Typography>
        </Box>
      )}

      {/* Modal d'annulation enrichi propriétaire avec raisons */}
      <CancellationModalEnhanced
        open={showCancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleOwnerCancellationWithReason}
        title={t('modalTitle')}
        reservation={reservationToCancel}
        context="owner_cancellation"
        userRole="owner"
        loading={loading}
        showRules={true}
        showReservationDetails={true}
      />

      {/* Modal des règles d'annulation propriétaire */}
      <CancellationRulesModal />

      {/* Modal de messagerie */}
      <Modal
        open={showMessaging}
        onClose={handleCloseMessaging}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showMessaging}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 600 },
              height: { xs: '80vh', sm: '70vh', md: '600px' },
              maxWidth: 600,
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden'
            }}
          >
            {selectedReservationForChat && (
              <MessagingInterface
                reservationId={selectedReservationForChat.id}
                reservation={selectedReservationForChat}
                currentUser={user}
                onClose={handleCloseMessaging}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Modal de détails de réservation avec messagerie intégrée */}
      <Modal
        open={showReservationDetails}
        onClose={handleCloseDetails}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showReservationDetails}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '96%', md: '94%', lg: '92%', xl: '90%' },
              height: { xs: '95vh', sm: '92vh', md: '90vh', lg: '88vh', xl: '85vh' },
              maxWidth: 1600,
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {selectedReservationForDetails && (
              <ReservationDetailsPage
                reservation={selectedReservationForDetails}
                currentUser={user}
                userRole="owner"
                scrollToTracking={shouldScrollToTracking}
                onBack={(hasChanges) => {
                  setShouldScrollToTracking(false); // Réinitialiser le flag
                  handleCloseDetails(hasChanges);
                }}
                onDataUpdate={async (updatedReservationData) => {
                  console.log('OwnerReservationsPage - onDataUpdate appelé avec:', updatedReservationData);
                  
                  // Si on a des données de réservation mises à jour, mettre à jour localement
                  if (updatedReservationData && updatedReservationData.id) {
                    setReservations(prevReservations => {
                      return prevReservations.map(res => {
                        if (res.id === updatedReservationData.id) {
                          // Mise à jour locale du card
                          return {
                            ...res,
                            ...updatedReservationData
                          };
                        }
                        return res;
                      });
                    });
                    
                    // Mettre à jour aussi la réservation dans le modal
                    setSelectedReservationForDetails(prev => ({
                      ...prev,
                      ...updatedReservationData
                    }));
                    
                    console.log('✅ Card mis à jour localement');
                  }
                }} // Callback pour rafraîchir les données du modal
                onReservationRefresh={handleReservationRefresh} // Callback pour rafraîchir la réservation spécifique
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modales QR Code */}
      {qrGeneratorOpen && qrReservation && (
        <QRCodeGeneratorModal
          open={qrGeneratorOpen}
          onClose={handleCloseQRGenerator}
          reservation={qrReservation}
          type={qrType}
          onGenerate={handleGenerateQR}
          loading={qrLoading}
        />
      )}

      {qrScannerOpen && qrReservation && (
        <QRCodeConfirmationModal
          open={qrScannerOpen}
          onClose={handleCloseQRScanner}
          reservation={qrReservation}
          type={qrType}
          expectedCode={qrType === 'pickup' ? qrReservation.client_code : qrReservation.owner_code}
          qrType={qrType}
          userRole="owner"
          onConfirm={handleConfirmation}
          onRefuse={handleRefusal}
          loading={qrLoading}
        />
      )}

      {/* Modal de confirmation d'expédition de livraison */}
      {shipmentModalOpen && shipmentReservation && (
        <Dialog
          open={shipmentModalOpen}
          onClose={closeShipmentModal}
          maxWidth="sm"
          fullWidth
          disableScrollLock={true}
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              paddingTop: '10vh'
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('delivery:confirmShipment')}
            <IconButton onClick={closeShipmentModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DeliveryTrackingForm
              reservationId={shipmentReservation.id}
              onSuccess={handleShipmentSuccess}
              onCancel={closeShipmentModal}
              onStartLoading={handleStartShipmentLoading}
              onError={handleShipmentError}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Drawer Sidebar - Seulement si pas embedded */}
      {!embedded && (
        <>
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="temporary"
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: { xs: '100%', md: 350 },
                maxWidth: '100vw',
                height: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
              },
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(4px)'
              }
            }}
          >
            <SidebarContent />
          </Drawer>

          {/* FAB flottant pour ouvrir le sidebar */}
          {!sidebarOpen && (
            <Tooltip title={t('sidebar.open', 'Ouvrir le menu réservations')} placement="left">
              <Fab
                color="primary"
                sx={{
                  position: 'fixed',
                  top: '50%',
                  right: 24,
                  transform: 'translateY(-50%)',
                  zIndex: 1301,
                  animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  '&:hover': {
                    animation: 'none',
                    transform: 'translateY(-50%) scale(1.15)',
                    boxShadow: '0 12px 25px rgba(98, 0, 238, 0.6)',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(45deg, #6200ee 40%, #03dac4 80%)'
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(0.95)',
                    transition: 'all 0.1s ease'
                  }
                }}
                onClick={toggleSidebar}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </Fab>
            </Tooltip>
          )}
        </>
      )}

      {/* Modal d'évaluation du renter */}
      {selectedReservationForEval && (
        <RenterEvaluationForm
          open={evaluationModalOpen}
          onClose={handleCloseRenterEvaluation}
          reservation={selectedReservationForEval}
          existingReview={existingReviewForEdit}
          onSuccess={handleRenterEvaluationSuccess}
        />
      )}
      </ContentWrapper>
    </>
  );
};

export default OwnerReservationsPage;
